import { faChevronRight, faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useActionModalStore } from '@gen2/app/components/action-modal/store';
import { TInviteRequestsForm } from '@gen2/app/invites/send-invites/requests/requests';
import { useRequestsStore } from '@gen2/app/invites/send-invites/requests/store';
import { useSendInviteStore } from '@gen2/app/invites/send-invites/store';
import { useToast } from '@gen2/hooks';
import {
  AccordionProps,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Tooltip,
} from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useConfirmationModalStore } from '../confirmation-modal/hooks/confirmation-modal-store';
import { TRequestContainer, types, useRequestHook } from './hooks/request';
import {
  StyledErrorBox,
  StyledIconButton,
  StyledInviteRequest,
  StyledInviteRequestBody,
  StyledInviteRequestHead,
  StyledTitle,
  StyledType,
  StyledTypesContainer,
} from './invite-request.styles';

export type InviteRequestProps = {
  hasDelete?: boolean;
  isDeleteDisabled?: boolean;
  id: number; // index
  container?: TRequestContainer;
  isGroupExpanded?: boolean;
} & Omit<AccordionProps, 'children' | 'id'>;

export const InviteRequest = React.memo(
  ({
    hasDelete,
    isDeleteDisabled,
    id,
    container = 'invite',
    isGroupExpanded,
  }: InviteRequestProps) => {
    const {
      formState: { errors },
      getValues,
      register,
      setValue,
    } = useFormContext<TInviteRequestsForm>();

    const req = getValues().requests[id];

    const { setToDeleteRequest } = useRequestsStore();
    const { onUpdate, onChangeVariant } = useRequestHook({ id, container });
    const { setIsConfirmationModalOpen } = useConfirmationModalStore();
    const { showModal } = useActionModalStore();
    const siStore = useSendInviteStore();
    const toast = useToast();

    const { t } = useTranslation('sendInvite');

    // Simple expansion state management
    const [localExpanded, setLocalExpanded] = React.useState(false);

    React.useEffect(() => {
      if (isGroupExpanded) {
        setLocalExpanded(true);
      } else {
        setLocalExpanded(req?.expanded ?? false);
      }
    }, [isGroupExpanded, req?.expanded]);

    // Sync with form value
    React.useEffect(() => {
      // Use default value of false if expanded is undefined
      setLocalExpanded(req?.expanded ?? false);
    }, [req?.expanded]);

    const stopPropagation = useMemo(() => {
      return (e: React.MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
        e.stopPropagation();
      };
    }, []);

    const handleDelete = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();

        setToDeleteRequest(req);
        setIsConfirmationModalOpen(true);
      },
      [req, setToDeleteRequest, setIsConfirmationModalOpen],
    );

    const onSelectType = useCallback(
      async (event: SelectChangeEvent) => {
        event.stopPropagation();

        const handleUpdate = (isConfirmed: boolean) => {
          if (!isConfirmed) return;

          onChangeVariant(event.target.value);
        };

        if (siStore.isEditSendInvite) {
          const { isConfirmed } = await showModal({
            header: t('editSendInvite.changeType.title'),
            message: 'editSendInvite.changeType.message',
            translationNamespace: 'sendInvite',
            closeButtonLabel: t('editSendInvite.changeType.cancel') ?? '',
            submitButtonLabel: t('editSendInvite.changeType.confirm') ?? '',
          });

          if (isConfirmed !== undefined) {
            handleUpdate(isConfirmed);
          }
        } else {
          handleUpdate(true);
        }
      },
      [onChangeVariant, showModal, siStore.isEditSendInvite, t],
    );

    const onToggleRequest = useCallback(() => {
      const newExpanded = !localExpanded;

      // Update local state immediately
      setLocalExpanded(newExpanded);

      // Update form value
      try {
        if (typeof id === 'number' && id >= 0) {
          const path = `requests.${id}.expanded`;
          setValue(path as any, newExpanded);
        }
      } catch (error) {
        toast.show({
          text: 'Failed to update request state',
          variant: 'error',
        });
      }
    }, [localExpanded, req.expanded, setValue, id]);

    const Variant = useMemo(() => {
      if (!req) return null;

      const CurrentVariant = types.find((t) => t.value === req.type)?.component;

      if (!CurrentVariant) return null;

      return <CurrentVariant id={id} container={container} />;
    }, [id, req, container]);

    const handleFocusTitle = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        const defaultTitles = [
          'New File Upload Request',
          'New Read Only Request',
          'New Form Request',
        ];
        if (defaultTitles.includes(e.target.value)) {
          e.target.value = '';
        }
      },
      [],
    );

    // Use local expansion state directly
    const isExpanded = localExpanded;

    return (
      <>
        <StyledInviteRequest
          $hasError={!!(errors?.requests && errors.requests[id])}
          square
          disableGutters
          expanded={isExpanded}
          onChange={onToggleRequest}
          data-cy="invite-request"
        >
          <StyledInviteRequestHead
            expandIcon={
              <FontAwesomeIcon
                icon={faChevronRight}
                data-cy={`request-expand-icon-${id}`}
                size="sm"
              />
            }
          >
            <StyledTitle
              fullWidth
              variant="filled"
              size="small"
              onClick={stopPropagation}
              onFocus={handleFocusTitle}
              {...register(`requests.${id}.title`, {
                onBlur: onUpdate,
              })}
              data-cy={`request-title-${id}`}
              inputProps={{
                'data-cy': 'request-title-input',
              }}
            />
            <StyledTypesContainer onClick={stopPropagation}>
              <FormControl fullWidth color="info" size="small">
                <InputLabel id="request-type">Request Type</InputLabel>
                <Select
                  data-cy="request-type-menu"
                  inputProps={{
                    'data-cy': 'request-type',
                  }}
                  fullWidth
                  label="Request Type"
                  value={req?.type || ''}
                  {...register(`requests.${id}.type`)}
                  onChange={onSelectType}
                >
                  {types.map((t) => (
                    <StyledType key={t.value} value={t.value}>
                      {t.label}
                    </StyledType>
                  ))}
                </Select>
              </FormControl>
            </StyledTypesContainer>
            {hasDelete && (
              <Tooltip title="Delete" arrow placement="top">
                <span>
                  <StyledIconButton
                    onClick={handleDelete}
                    disabled={isDeleteDisabled}
                    color="tonalLight"
                  >
                    <FontAwesomeIcon icon={faTrashCan} size="sm" />
                  </StyledIconButton>
                </span>
              </Tooltip>
            )}
          </StyledInviteRequestHead>

          <StyledInviteRequestBody>{Variant}</StyledInviteRequestBody>
        </StyledInviteRequest>
        {!!errors.requests && errors.requests[id]?.title && (
          <StyledErrorBox data-cy="error">
            {t(errors.requests[id]?.title?.message || '')}
          </StyledErrorBox>
        )}
      </>
    );
  },
);
